/* eslint-disable complexity */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { isEqual } from 'lodash';

import { FormulaAssumption } from '@futrli-sdk/consolidations';
import { useCrudActivityContext } from '@futrli/shared-services';

import { getOutputOccursOnData, checkFormulaValidation, getAvailableLineData } from './utils';
import { AllowedAssumptionTypes } from '../../../../detail/reports/actions/actions';
import { FormulaError, PaymentTreatment, PredictionType, RepeatingPattern } from '../../../types';
import { FormulaMethod } from '../../../components/formula/formula-method';
import { FormulaReferenceOption } from '../../../../../utils/formulas/types';
import { PredictionFormulaError } from '../../../components/formula/components/prediction-formula-error';
import {
  ASSUMPTION_TYPES,
  LAST_DAY_OF_MONTH,
  LAST_DAY_OF_MONTH_NUM,
  TRACKING_EVENTS
} from '../../../../../core/constants';
import { PredictionEditor } from '../prediction-editor/prediction-editor';
import { PredictionHeader } from '../prediction-header/prediction-header';
import { PROFIT_LOSS_ID, BALANCE_SHEET_ID, NON_FINANCIAL_DRIVERS_ID } from '../../../components/formula/const';
import { trackEvent } from '../../../../../core/tracker';
import { useConsolidatedOrganisationPredictionBuilderContext } from '../../../context/consolidated-organisation-prediction-builder-context';
import { deLocalizeFormula, localizeFormula } from '../../../../../utils/formulas/i18n-utils';

interface Props {
  onClose?: () => void;
  isPredictionTypeActive?: boolean;
}

export const FormulaPrediction = ({ onClose, isPredictionTypeActive }: Props) => {
  const intl = useIntl();

  const {
    accountIndex,
    entity,
    forecast,
    handleSaveExisting,
    handleSaveNew,
    isCashFlowDisabled,
    isEditing,
    isQuickCopy,
    lineData,
    paymentTreatment,
    predictionData,
    predictionName,
    predictionType,
    reloadForecastData,
    reset,
    resetUrlData,
    setFormulaHasRefs,
    setImpactPreviewParams,
    setIsSaveDisabled,
    setPredictionContentTouched,
    urlPredictionId,
    vatPercentage,
    previewImpactsResponse
  } = useConsolidatedOrganisationPredictionBuilderContext();

  const { addCreating, removeCreating } = useCrudActivityContext();

  const [formulaContent, setFormulaContent] = useState('');
  const [validFormulaContent, setValidFormulaContent] = useState('');

  const [formulaContentTouched, setFormulaContentTouched] = useState(false);
  const [formulaError, setFormulaError] = useState({ error_code: FormulaError.NO_ERROR, is_valid: true });
  const [formulaPreviewImpactError, setFormulaPreviewImpactError] = useState({
    error_code: FormulaError.NO_ERROR,
    is_valid: true
  });
  const [formulaFrequencyOnDay, setFormulaFrequencyOnDay] = useState(LAST_DAY_OF_MONTH);
  const [formulaFrequencyOnMonth, setFormulaFrequencyOnMonth] = useState('1');
  const [formulaEditorVersion, setFormulaEditorVersion] = useState(0);
  const [isContentInitialized, setIsContentInitialized] = useState(false);
  const [initialFormulaContent, setInitialFormulaContent] = useState('');
  const [outputFrequency, setOutputFrequency] = useState<RepeatingPattern>(RepeatingPattern.monthly);

  const isActive = predictionType === PredictionType.FORMULA;
  const isInvalidFormula = !formulaError.is_valid || formulaContent.length === 0 || formulaContent === '\n';
  const isInvalidFormulaFromPreviewImpacts = !previewImpactsResponse?.is_valid;

  const isFirstRun = useRef(true);
  const lastImpactPreviewParams = useRef<Partial<AllowedAssumptionTypes>>();
  const onlyShowProfitAndLoss = forecast?.kind === 'budget';

  const referenceOptions = useMemo((): FormulaReferenceOption[] | undefined => {
    if (lineData) {
      const parsedInitialOptions = getAvailableLineData(
        lineData.profit_and_loss,
        intl.formatMessage({ id: 'financial_fields.type.profit_and_loss', defaultMessage: 'Profit & Loss' }),
        lineData.balance_sheet,
        intl.formatMessage({ id: 'financial_fields.type.balance_sheet', defaultMessage: 'Balance Sheet' }),
        onlyShowProfitAndLoss
      );

      // Formulas shouldn't be able to directly @reference themselves, so filter that out
      if (isEditing && urlPredictionId) {
        const filteredOptions = parsedInitialOptions.filter(o => o.id.indexOf(urlPredictionId) < 0);
        return filteredOptions;
      } else {
        return parsedInitialOptions;
      }
    }
  }, [lineData, onlyShowProfitAndLoss, isEditing, urlPredictionId, intl]);

  const formulaErrorForDisplay = useMemo(() => {
    if (!formulaPreviewImpactError.is_valid) {
      return formulaPreviewImpactError;
    }
    return formulaError;
  }, [formulaError, formulaPreviewImpactError]);

  // *********
  // * Utils *
  // *********

  const getSaveParams = useCallback(() => {
    const params: Partial<FormulaAssumption> = {
      name: predictionName,
      vat_percentage: paymentTreatment === PaymentTreatment.NON_CASH_TRANSFER ? '0' : vatPercentage || null,
      account_id: accountIndex,
      excluded_from_report: false,
      formula: deLocalizeFormula(validFormulaContent),
      output_frequency: outputFrequency as FormulaAssumption['output_frequency'],
      ...getOutputOccursOnData(outputFrequency, formulaFrequencyOnDay, formulaFrequencyOnMonth)
    };
    return params;
  }, [
    accountIndex,
    formulaFrequencyOnDay,
    formulaFrequencyOnMonth,
    outputFrequency,
    paymentTreatment,
    predictionName,
    validFormulaContent,
    vatPercentage
  ]);

  const refreshAccountsImpactPreview = useCallback(() => {
    const validFormulaContentEmpty = !validFormulaContent || validFormulaContent === '\n';

    // if cashflow disabled / current formula is not valid, do not refresh data
    if (isCashFlowDisabled || isInvalidFormula || validFormulaContentEmpty) {
      return;
    }

    if (accountIndex && predictionName) {
      const newParams = {
        ...getSaveParams(),
        name: 'formula-impact-preview'
      };

      if (!isEqual(newParams, lastImpactPreviewParams.current)) {
        lastImpactPreviewParams.current = newParams;
        setImpactPreviewParams(newParams);
      }
    }
  }, [
    accountIndex,
    getSaveParams,
    isCashFlowDisabled,
    isInvalidFormula,
    predictionName,
    setImpactPreviewParams,
    validFormulaContent
  ]);

  const restoreInitialContent = useCallback(() => {
    setOutputFrequency(RepeatingPattern.monthly);
    setFormulaContent('');
    setValidFormulaContent('');
    setFormulaContentTouched(false);
    setFormulaFrequencyOnDay(LAST_DAY_OF_MONTH);
    setFormulaFrequencyOnMonth('1');
    setFormulaError({ error_code: FormulaError.NO_ERROR, is_valid: true });
    setInitialFormulaContent('');
    setFormulaEditorVersion(version => version + 1);
    setIsContentInitialized(true);
    return true;
  }, []);

  const restoreLoadedContent = useCallback(() => {
    const formulaFetchedPrediction = predictionData?.assumption_data as FormulaAssumption | undefined;

    if (formulaFetchedPrediction && referenceOptions) {
      setOutputFrequency(formulaFetchedPrediction.output_frequency as RepeatingPattern);
      if (typeof formulaFetchedPrediction.output_occurs_on_day === 'number') {
        setFormulaFrequencyOnDay(
          formulaFetchedPrediction.output_occurs_on_day === LAST_DAY_OF_MONTH_NUM
            ? LAST_DAY_OF_MONTH
            : String(formulaFetchedPrediction.output_occurs_on_day)
        );
      }
      if (typeof formulaFetchedPrediction.output_occurs_on_month === 'number') {
        setFormulaFrequencyOnMonth(String(formulaFetchedPrediction.output_occurs_on_month));
      }
      const localizedFormula = localizeFormula(formulaFetchedPrediction.formula);
      setFormulaContent(localizedFormula);
      setInitialFormulaContent(localizedFormula);
      setFormulaEditorVersion(version => version + 1);
      setIsContentInitialized(true);
      return true;
    }
    return false;
  }, [predictionData, referenceOptions]);

  const save = useCallback(async () => {
    const params = getSaveParams();

    let created = false;
    if (urlPredictionId && !isQuickCopy) {
      created = await handleSaveExisting(params);
    } else {
      created = !!(await handleSaveNew(params));
      if (created) {
        trackEvent(TRACKING_EVENTS.CREATED_ANY_MANUAL_PREDICTION);
        trackEvent(TRACKING_EVENTS.ADD_PREDICTION_FORMULA_CONSOLIDATION);
      }
    }

    return created;
  }, [getSaveParams, handleSaveExisting, handleSaveNew, isQuickCopy, urlPredictionId]);

  const validateFormula = useCallback(() => {
    if (formulaContentTouched) {
      const contentWithReplacedValues = formulaContent
        .replace(PROFIT_LOSS_ID, '')
        .replace(BALANCE_SHEET_ID, '')
        .replace(NON_FINANCIAL_DRIVERS_ID, '');

      const isValidFormula = checkFormulaValidation(contentWithReplacedValues, outputFrequency);
      setFormulaError({
        error_code: isValidFormula ? FormulaError.NO_ERROR : FormulaError.ERROR_INVALID_SYNTAX,
        is_valid: isValidFormula
      });

      if (previewImpactsResponse) {
        setFormulaPreviewImpactError({
          error_code: previewImpactsResponse.error_code,
          is_valid: previewImpactsResponse.is_valid
        });
      }

      if (isValidFormula) {
        setValidFormulaContent(formulaContent);
      }

      return isValidFormula;
    }
  }, [formulaContent, formulaContentTouched, outputFrequency, previewImpactsResponse]);

  // ************
  // * Handlers *
  // ************

  const handleFormulaContentChange = useCallback(
    (content: string) => {
      if (content !== formulaContent) {
        setFormulaContent(content);
        setFormulaHasRefs(!!content.match(/REF\(([^)]+)\)/g));
        if (content && content !== '\n') {
          setPredictionContentTouched(true);
          setFormulaContentTouched(true);
        }
      }
    },
    [formulaContent, setFormulaHasRefs, setPredictionContentTouched]
  );

  const handleOutputFrequencyChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPredictionContentTouched(true);
    setOutputFrequency(event.target.value as RepeatingPattern);
    setFormulaFrequencyOnDay(LAST_DAY_OF_MONTH);
  };

  const handleFormulaFrequencyOnDayChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPredictionContentTouched(true);
    setFormulaFrequencyOnDay(event.target.value);
  };

  const handleFormulaFrequencyOnMonthChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPredictionContentTouched(true);
    setFormulaFrequencyOnMonth(event.target.value);
  };

  const handleSave = useCallback(async () => {
    addCreating('formula-prediction');
    const success = await save();
    if (success) {
      reloadForecastData();
      onClose?.();
    }
    removeCreating('formula-prediction');
  }, [addCreating, onClose, reloadForecastData, removeCreating, save]);

  const handleSaveAndNew = useCallback(async () => {
    addCreating('formula-prediction');
    const success = await save();
    if (success) {
      reloadForecastData();
      reset();
      resetUrlData();
      restoreInitialContent();
    }
    removeCreating('formula-prediction');
  }, [addCreating, reloadForecastData, removeCreating, reset, resetUrlData, restoreInitialContent, save]);

  // ***********
  // * Effects *
  // ***********

  // Set initial / restore saved content on first run
  useEffect(() => {
    if (isFirstRun.current) {
      let contentWasRestored = false;

      if (isEditing || isQuickCopy) {
        if (predictionData) {
          if (predictionData.assumption_type === ASSUMPTION_TYPES.FORMULA) {
            contentWasRestored = restoreLoadedContent();
          } else {
            contentWasRestored = restoreInitialContent();
          }
        }
      } else {
        contentWasRestored = restoreInitialContent();
      }

      isFirstRun.current = !contentWasRestored;
    }
  }, [isEditing, predictionData, restoreInitialContent, restoreLoadedContent, isQuickCopy]);

  // Validate data if content changes
  useEffect(() => {
    if (isActive) {
      validateFormula();
    }
  }, [isActive, validateFormula, previewImpactsResponse]);

  // Reload impact data if valid data changes
  useEffect(() => {
    if (isActive) {
      refreshAccountsImpactPreview();
    } else {
      lastImpactPreviewParams.current = undefined;
    }
  }, [isActive, refreshAccountsImpactPreview]);

  // Update isSaveDisabled flag
  useEffect(() => {
    if (isActive) {
      setIsSaveDisabled(isInvalidFormula || isInvalidFormulaFromPreviewImpacts);
    }
  }, [isActive, isInvalidFormula, isInvalidFormulaFromPreviewImpacts, setIsSaveDisabled]);

  return (
    <PredictionEditor
      footerContent={
        <PredictionFormulaError
          formulaContent={formulaContent}
          formulaError={formulaErrorForDisplay}
          show={formulaContentTouched}
        />
      }
      formError={!formulaError.is_valid && formulaContentTouched}
      onSave={handleSave}
      onSaveAndNew={handleSaveAndNew}
      show={isActive}
      onClose={onClose}
      isPredictionTypeActive={isPredictionTypeActive}
    >
      {isContentInitialized && (
        <FormulaMethod
          key={formulaEditorVersion}
          PredictionHeader={PredictionHeader}
          handleFormulaContentChange={handleFormulaContentChange}
          outputFrequency={outputFrequency}
          handleOutputFrequencyChange={handleOutputFrequencyChange}
          formulaFrequencyOnDay={formulaFrequencyOnDay}
          handleFormulaFrequencyOnDayChange={handleFormulaFrequencyOnDayChange}
          formulaFrequencyOnMonth={formulaFrequencyOnMonth}
          handleFormulaFrequencyOnMonthChange={handleFormulaFrequencyOnMonthChange}
          initialEditorOptions={referenceOptions || []}
          initialFormulaContent={initialFormulaContent}
          message={
            !entity ? (
              <FormattedMessage
                id="predict.please_choose_a_sub_entity_to_continue"
                defaultMessage="To continue, please choose a sub-entity."
              />
            ) : undefined
          }
        />
      )}
    </PredictionEditor>
  );
};
